import React, { useState, useEffect, useRef } from "react";
import { API_BASE_URL } from "./utils/api";
import TopBar from "./components/TopBar";
import ChatWindow from "./components/ChatWindow";
import MessageInput from "./components/MessageInput";

import LeftSidebar from "./components/LeftSidebar";
import SettingsModal from "./components/SettingsModal";
import FilesModal from "./components/FilesModal";
import LoginScreen from "./components/LoginScreen";
import RegisterScreen from "./components/RegisterScreen";
import PaymentScreen from "./components/PaymentScreen";
import WelcomeScreen from "./components/WelcomeScreen";
import { useAuth } from "./contexts/AuthContext";
import { testAPI } from "./utils/apiTest";
import { documentAPI, chatAPI, chatSessionsAPI, api, fetchRelevantClausesForMessage } from "./utils/api";
import ConfirmationModal from "./components/ConfirmationModal";
import ResetPassword from "./components/ResetPassword";
import EmployeesModal from "./components/EmployeesModal";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Dashboard from "./components/Dashboard";

function ThankYouModal({ isOpen, onClose }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm animate-fade-in">
      {/* Confetti animation */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden z-10">
        <div className="absolute left-1/4 top-1/4 animate-confetti1 w-32 h-32 bg-gradient-to-br from-pink-400 to-yellow-300 rounded-full opacity-70 blur-2xl"></div>
        <div className="absolute right-1/4 top-1/3 animate-confetti2 w-24 h-24 bg-gradient-to-br from-blue-400 to-green-300 rounded-full opacity-60 blur-2xl"></div>
        <div className="absolute left-1/3 bottom-1/4 animate-confetti3 w-20 h-20 bg-gradient-to-br from-purple-400 to-indigo-300 rounded-full opacity-50 blur-2xl"></div>
      </div>
      <div className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 p-10 max-w-md w-full flex flex-col items-center animate-modal-pop">
        {/* Animated glowing checkmark */}
        <div className="mb-4 relative flex items-center justify-center">
          <span className="absolute animate-pulse-glow w-24 h-24 rounded-full bg-gradient-to-br from-green-400/40 to-green-600/20 blur-2xl"></span>
          <svg className="w-20 h-20 text-green-500 animate-checkmark-pop" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" className="opacity-30" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M8 12l2.5 2.5L16 9" className="animate-checkmark-stroke" />
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2 text-center tracking-tight">Thank you for your purchase!</h2>
        <p className="text-gray-600 dark:text-gray-300 text-center mb-6 text-lg">Your subscription is now active. Enjoy all the Pro features!</p>
        <button
          onClick={onClose}
          className="px-8 py-3 rounded-2xl bg-gradient-to-r from-indigo-600 to-blue-500 text-white font-bold shadow-lg hover:from-indigo-700 hover:to-blue-600 transition text-lg mt-2"
        >
          Close
        </button>
      </div>
      <style>{`
        @keyframes fade-in { 0% { opacity: 0; } 100% { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.5s ease; }
        @keyframes modal-pop { 0% { transform: scale(0.8); opacity: 0; } 60% { transform: scale(1.05); opacity: 1; } 100% { transform: scale(1); } }
        .animate-modal-pop { animation: modal-pop 0.6s cubic-bezier(0.68, -0.55, 0.27, 1.55); }
        @keyframes checkmark-pop { 0% { transform: scale(0.5); opacity: 0; } 80% { transform: scale(1.2); opacity: 1; } 100% { transform: scale(1); } }
        .animate-checkmark-pop { animation: checkmark-pop 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55); }
        @keyframes checkmark-stroke { 0% { stroke-dasharray: 0, 24; } 100% { stroke-dasharray: 24, 0; } }
        .animate-checkmark-stroke { stroke-dasharray: 24, 0; animation: checkmark-stroke 0.7s 0.2s cubic-bezier(0.68, -0.55, 0.27, 1.55) forwards; }
        @keyframes pulse-glow { 0%, 100% { opacity: 0.7; transform: scale(1); } 50% { opacity: 1; transform: scale(1.15); } }
        .animate-pulse-glow { animation: pulse-glow 2s infinite; }
        @keyframes confetti1 { 0% { transform: translateY(-40px) scale(0.8); opacity: 0.7; } 100% { transform: translateY(40px) scale(1.1); opacity: 0.2; } }
        .animate-confetti1 { animation: confetti1 2.5s infinite alternate; }
        @keyframes confetti2 { 0% { transform: translateY(-30px) scale(0.7); opacity: 0.6; } 100% { transform: translateY(30px) scale(1.2); opacity: 0.2; } }
        .animate-confetti2 { animation: confetti2 2.2s infinite alternate; }
        @keyframes confetti3 { 0% { transform: translateY(30px) scale(0.7); opacity: 0.5; } 100% { transform: translateY(-30px) scale(1.1); opacity: 0.2; } }
        .animate-confetti3 { animation: confetti3 2.8s infinite alternate; }
      `}</style>
    </div>
  );
}

function ChatLimitModal({ isOpen, isAdmin, onClose, onUpgrade }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm animate-fade-in">
      {/* Confetti/Glow background for premium feel */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden z-10">
        <div className="absolute left-1/4 top-1/4 animate-confetti1 w-24 h-24 bg-gradient-to-br from-yellow-300/60 to-pink-200/40 rounded-full blur-2xl"></div>
        <div className="absolute right-1/4 top-1/3 animate-confetti2 w-20 h-20 bg-gradient-to-br from-indigo-200/60 to-blue-100/40 rounded-full blur-2xl"></div>
        <div className="absolute left-1/3 bottom-1/4 animate-confetti3 w-16 h-16 bg-gradient-to-br from-purple-200/60 to-indigo-100/40 rounded-full blur-2xl"></div>
      </div>
      <div className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-800 p-10 max-w-md w-full flex flex-col items-center animate-modal-pop">
        {/* Animated warning icon with glow */}
        <div className="mb-4 relative flex items-center justify-center">
          <span className="absolute animate-pulse-glow w-20 h-20 rounded-full bg-gradient-to-br from-yellow-300/40 to-yellow-500/20 blur-2xl"></span>
          <svg className="w-16 h-16 text-yellow-400 animate-bounce-smooth drop-shadow-lg" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" fill="none" className="opacity-30" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 8v4m0 4h.01" />
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2 text-center tracking-tight">Limit Reached</h2>
        <p className="text-gray-600 dark:text-gray-300 text-center mb-7 text-lg font-medium">
          You have consumed your allocated limit.<br />
          {isAdmin
            ? <span className="text-indigo-600 dark:text-indigo-300 font-semibold">Upgrade to Pro to continue using chat features.</span>
            : <span className="text-rose-500 dark:text-rose-300 font-semibold">Please contact your company administrator to upgrade your plan.</span>}
        </p>
        <div className="flex gap-4 w-full justify-center">
          <button
            onClick={onClose}
            className="px-7 py-3 rounded-2xl bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 font-bold shadow hover:bg-gray-200 dark:hover:bg-gray-700 transition text-lg border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            Close
          </button>
          {isAdmin && (
            <button
              onClick={onUpgrade}
              className="px-7 py-3 rounded-2xl bg-gradient-to-r from-indigo-600 to-blue-500 text-white font-bold shadow-lg hover:from-indigo-700 hover:to-blue-600 transition text-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              Upgrade to Pro
            </button>
          )}
        </div>
      </div>
      <style>{`
        @keyframes fade-in { 0% { opacity: 0; } 100% { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.4s ease; }
        @keyframes modal-pop { 0% { transform: scale(0.85); opacity: 0; } 60% { transform: scale(1.05); opacity: 1; } 100% { transform: scale(1); } }
        .animate-modal-pop { animation: modal-pop 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55); }
        @keyframes pulse-glow { 0%, 100% { opacity: 0.7; transform: scale(1); } 50% { opacity: 1; transform: scale(1.12); } }
        .animate-pulse-glow { animation: pulse-glow 2s infinite; }
        @keyframes confetti1 { 0% { transform: translateY(-30px) scale(0.8); opacity: 0.7; } 100% { transform: translateY(30px) scale(1.1); opacity: 0.2; } }
        .animate-confetti1 { animation: confetti1 2.2s infinite alternate; }
        @keyframes confetti2 { 0% { transform: translateY(-20px) scale(0.7); opacity: 0.6; } 100% { transform: translateY(20px) scale(1.2); opacity: 0.2; } }
        .animate-confetti2 { animation: confetti2 2.0s infinite alternate; }
        @keyframes confetti3 { 0% { transform: translateY(20px) scale(0.7); opacity: 0.5; } 100% { transform: translateY(-20px) scale(1.1); opacity: 0.2; } }
        .animate-confetti3 { animation: confetti3 2.4s infinite alternate; }
        @keyframes bounce-smooth { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        .animate-bounce-smooth { animation: bounce-smooth 1.2s infinite; }
      `}</style>
    </div>
  );
}

export default function App() {
  const { isAuthenticated, logout, user, setUser } = useAuth();
  
  // All useState hooks must be called before any conditional returns

  const [showLeftSidebar, setShowLeftSidebar] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [pendingLogout, setPendingLogout] = useState(false);
  const [showEmployeesModal, setShowEmployeesModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [showChatLimitModal, setShowChatLimitModal] = useState(false);
  const [isChatLimitAdmin, setIsChatLimitAdmin] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [selectedCodeBook, setSelectedCodeBook] = useState(null);
  
  // Global PDF processing progress (visible in TopBar)
  const [pdfProgress, setPdfProgress] = useState(null); // { documentId, documentName, mode, step, percent, status }
  const pdfEventSourceRef = useRef(null);
  const pdfSseAbortControllerRef = useRef(null);
  
  // Document refresh trigger
  const [documentRefreshTrigger, setDocumentRefreshTrigger] = useState(0);
  
  // Document and metadata state for RAG queries
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [documentMetadata, setDocumentMetadata] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isLoadingRAGResponse, setIsLoadingRAGResponse] = useState(false);
  
  // Streaming control state
  const [currentStreamControl, setCurrentStreamControl] = useState(null);

  // Chat history - start with empty state
  const [chatHistory, setChatHistory] = useState([]);

  // Initialize with no selected chat
  const [selectedChatId, setSelectedChatId] = useState(null);

  // Chat messages state - start empty
  const [messages, setMessages] = useState([]);

  // Function to trigger document list refresh
  const refreshDocuments = () => {
    setDocumentRefreshTrigger(prev => prev + 1);
    // Notify document selector to refresh
    window.dispatchEvent(new CustomEvent('documentsUpdated'));
  };

  // Load available documents and metadata for RAG queries
  const loadDocumentsForRAG = async () => {
    if (!isAuthenticated) return;

    try {
      console.log('🔄 Loading documents for RAG queries...');
      const documents = await documentAPI.getDocuments();
      setAvailableDocuments(documents);

      // Load metadata for all documents
      const metadataPromises = documents.map(async (doc) => {
        try {
          const metadata = await documentAPI.getMetadataRecord(doc.id);
          return {
            documentId: doc.id,
            documentName: doc.originalName,
            metadata: metadata, // lightweight record
            metadataId: metadata.metadataId
          };
        } catch (error) {
          console.warn(`Failed to load metadata for document ${doc.id}:`, error);
          return null;
        }
      });

      const allMetadata = await Promise.all(metadataPromises);
      const validMetadata = allMetadata.filter(Boolean);
      setDocumentMetadata(validMetadata);
      
      console.log('✅ Documents and metadata loaded for RAG:', {
        documentsCount: documents.length,
        metadataCount: validMetadata.length,
        metadataIds: validMetadata.map(m => m.metadataId).filter(Boolean)
      });
    } catch (error) {
      console.error('Failed to load documents for RAG:', error);
    }
  };

  const [currentSessionId, setCurrentSessionId] = useState(null);

  // Unified function to fetch relevant clauses for a chat message
  const fetchRelevantClauses = async (question, answer) => {
    if (!question || !answer) {
      console.log('❌ No question or answer provided for fetching clauses');
      return [];
    }
    
    try {
      console.log('🔍 Starting to fetch relevant clauses...');
      console.log('📊 Document metadata count:', documentMetadata.length);
      
      // Get metadata ID from the first selected document only, fallback to none if none selected
      const firstSelectedDoc = selectedDocuments.length > 0 ? selectedDocuments[0] : null;
      let metadataId = null;
      
      if (firstSelectedDoc) {
        // Find matching metadata for the selected document
        const metadata = documentMetadata.find(meta => meta.documentId === firstSelectedDoc.id);
        metadataId = metadata?.metadataId;
      }
      
      // If no metadata ID is found, don't use any
      const metadataIds = metadataId ? [metadataId] : [];

      console.log('📋 Available metadata ID:', metadataId);

      if (metadataIds.length === 0) {
        console.log('❌ No metadata IDs available for fetching clauses');
        return [];
      }

      console.log('🚀 Calling fetchRelevantClausesForMessage with', metadataIds.length, 'metadata IDs');
      
      // Use the utility function to fetch relevant clauses
      const clauses = await fetchRelevantClausesForMessage(question, answer, metadataIds);
      
      console.log('✅ Fetched clauses successfully:', clauses.length, 'clauses');
      return clauses;
    } catch (error) {
      console.error('❌ Failed to fetch relevant clauses for chat history:', error);
      return [];
    }
  };

  const handleChatSelect = async (id) => {
    setShowDashboard(false);
    setSelectedChatId(id);
    setCurrentSessionId(id);
    
    // Ensure documents and metadata are loaded before processing messages
    if (documentMetadata.length === 0) {
      console.log('🔄 Loading documents and metadata before processing chat history...');
      await loadDocumentsForRAG();
    }
    
    // Fetch session messages from API
    const session = await chatSessionsAPI.getSessionById(id);
    if (session && session.messages) {
      console.log('📋 Processing chat session messages:', session.messages.length, 'messages');
      
      // Process each message to include relevant clauses
      const processedMessages = [];
      
      for (const msg of session.messages) {
        // Add user message
        if (msg.question?.content) {
          processedMessages.push({
            sender: "user",
            text: msg.question.content,
          });
        }
        
        // Add AI message with relevant clauses
        if (msg.answer?.content) {
          // Check if relevantClauses already exist in the stored message
          let relevantClauses = msg.answer.metadata?.relevantClauses || msg.answer.relevantClauses || msg.relevantClauses || [];
          let isLoadingClauses = false;
          
          // If no clauses exist, fetch them
          if (relevantClauses.length === 0 && msg.question?.content) {
            console.log('🔍 Fetching relevant clauses for:', msg.question.content.substring(0, 50) + '...');
            isLoadingClauses = true;
            relevantClauses = await fetchRelevantClauses(msg.question.content, msg.answer.content);
            console.log('📄 Found', relevantClauses.length, 'relevant clauses');
          } else {
            console.log('📄 Using existing clauses:', relevantClauses.length, 'clauses');
          }
          
          processedMessages.push({
            sender: "ai",
            text: msg.answer.content,
            relevantClauses: relevantClauses,
            isLoadingClauses: isLoadingClauses,
          });
        }
      }
      
      console.log('✅ Processed messages with clauses:', processedMessages.length, 'messages');
      setMessages(processedMessages);
    } else {
      setMessages([]);
    }
  };

  const handleCreateNewChat = () => {
    setShowDashboard(false);
    setMessages([]);
    setSelectedChatId(null);
    setCurrentSessionId(null); // Reset sessionId for new chat
    console.log('✅ New chat started - screen cleared, no chat added to sidebar yet');
  };

  // Handle logout confirmation at the root level
  const handleLogoutConfirm = () => {
    setShowLogoutConfirm(false);
    setPendingLogout(true);
  };

  const handleOpenEmployeesModal = () => {
    setShowEmployeesModal(true);
  };

  // After modal is closed, if pendingLogout is true, call logout
  useEffect(() => {
    if (!showLogoutConfirm && pendingLogout) {
      logout();
      setPendingLogout(false);
    }
  }, [showLogoutConfirm, pendingLogout, logout]);

  // Test API configuration on app load
  useEffect(() => {
    testAPI();
  }, []);

  // Expose modal handlers globally for TopBar access
  useEffect(() => {
    window.openEmployeesModal = handleOpenEmployeesModal;
    // Expose PDF progress starter globally if needed elsewhere
    window.startPdfProcessingProgress = (documentId, documentName, mode) => {
      startPdfProcessingProgress(documentId, documentName, mode);
    };
    // Expose payment modal opener for other components (e.g., FilesModal) to trigger upgrades
    window.openPaymentModal = () => setShowPayment(true);
    // Expose files modal opener for document selector
    window.openFilesModal = () => setShowFilesModal(true);
    return () => {
      delete window.openEmployeesModal;
      delete window.startPdfProcessingProgress;
      delete window.openPaymentModal;
      delete window.openFilesModal;
    };
  }, []);

  // Load documents and metadata for RAG queries when authenticated or documents change
  useEffect(() => {
    if (isAuthenticated) {
      loadDocumentsForRAG();
    }
  }, [isAuthenticated, documentRefreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset chat state when user changes (login/logout)
  useEffect(() => {
    setMessages([]);
    setChatHistory([]);
    setSelectedChatId(null);
    setCurrentSessionId(null);
    setAvailableDocuments([]);
    setDocumentMetadata([]);
  }, [user]);

  // Start progress stream for PDF processing using fetch with Authorization header
  const startPdfProcessingProgress = (documentId, documentName, mode = 'balanced') => {
    try {
      // Close previous EventSource stream if any
      if (pdfEventSourceRef.current) {
        try { pdfEventSourceRef.current.close(); } catch (e) {}
        pdfEventSourceRef.current = null;
      }
      // Abort previous fetch stream if any
      if (pdfSseAbortControllerRef.current) {
        try { pdfSseAbortControllerRef.current.abort(); } catch (e) {}
        pdfSseAbortControllerRef.current = null;
      }
      setPdfProgress({
        documentId,
        documentName,
        mode,
        step: 'Connecting…',
        percent: 0,
        status: 'running',
        startedAt: Date.now(),
      });

      const token = localStorage.getItem('accessToken');
      const url = `${API_BASE_URL}/api/pdf/process/${documentId}/progress`;
      const controller = new AbortController();
      pdfSseAbortControllerRef.current = controller;

      const connect = async () => {
        try {
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Accept': 'text/event-stream',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            credentials: 'include',
            signal: controller.signal,
          });

          if (!response.ok) {
            if (response.status === 401) {
              setPdfProgress(prev => prev ? { ...prev, step: 'Unauthorized', status: 'error' } : prev);
            } else {
              setPdfProgress(prev => prev ? { ...prev, step: `Error ${response.status}`, status: 'error' } : prev);
            }
            return;
          }

          setPdfProgress(prev => prev ? { ...prev, step: 'Connected. Waiting for updates…', status: 'running' } : prev);

          const reader = response.body.getReader();
          const decoder = new TextDecoder('utf-8');
          let buffer = '';

          const processEvent = (raw) => {
            if (!raw) return;
            let eventName = 'message';
            const dataLines = [];
            raw.split('\n').forEach(line => {
              if (line.startsWith('event:')) {
                eventName = line.slice(6).trim();
              } else if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trim());
              }
            });
            const dataStr = dataLines.join('\n');
            let dataObj = {};
            try { dataObj = dataStr ? JSON.parse(dataStr) : {}; } catch (_) {}

            // Normalize completion/end conditions
            const stageLower = typeof dataObj?.stage === 'string' ? dataObj.stage.toLowerCase() : null;
            const stepLower = typeof dataObj?.step === 'string' ? dataObj.step.toLowerCase() : null;
            const statusLower = typeof dataObj?.status === 'string' ? dataObj.status.toLowerCase() : null;
            const percentValue = typeof dataObj?.percent === 'number' ? dataObj.percent : (typeof dataObj?.progress === 'number' ? dataObj.progress : undefined);
            const isCompletedEvent = (
              eventName === 'complete' ||
              eventName === 'done' ||
              statusLower === 'completed' ||
              stepLower === 'completed' || stepLower === 'done' ||
              stageLower === 'completed' || stageLower === 'done' ||
              percentValue === 100
            );
            if (isCompletedEvent) {
              const metaId = dataObj?.metadataId || dataObj?.data?.metadataId;
              setPdfProgress(prev => prev ? { ...prev, step: 'Completed', percent: 100, status: 'completed', completedAt: Date.now(), metadataId: metaId || prev.metadataId } : prev);
              try { controller.abort(); } catch (e) {}
              pdfSseAbortControllerRef.current = null;
              setTimeout(() => setPdfProgress(null), 3000);
              return;
            }

            if (eventName === 'connected') {
              setPdfProgress(prev => prev ? { ...prev, step: 'Connected', status: 'running' } : prev);
              return;
            }

            if (eventName === 'error') {
              setPdfProgress(prev => prev ? { ...prev, step: dataObj?.message || 'Error', status: 'error' } : prev);
              return;
            }

            if (eventName === 'progress' || eventName === 'message') {
              setPdfProgress(prev => {
                if (!prev) return prev;
                const nextStep = dataObj.message || dataObj.step || dataObj.stage || prev.step;
                const nextPercent = (typeof dataObj.percent === 'number')
                  ? Math.max(0, Math.min(100, dataObj.percent))
                  : (typeof dataObj.progress === 'number' ? Math.max(0, Math.min(100, dataObj.progress)) : prev.percent);
                return {
                  ...prev,
                  step: nextStep,
                  percent: nextPercent,
                  status: 'running',
                };
              });
            }
          };

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let idx;
            while ((idx = buffer.indexOf('\n\n')) !== -1) {
              const rawEvent = buffer.slice(0, idx).trim();
              buffer = buffer.slice(idx + 2);
              processEvent(rawEvent);
            }
          }

          // Stream ended; try reconnect if still running
          setPdfProgress(prev => prev && prev.status === 'running' ? { ...prev, step: 'Reconnecting…', status: 'reconnecting' } : prev);
          setTimeout(() => {
            if (pdfSseAbortControllerRef.current) {
              connect();
            }
          }, 1500);
        } catch (err) {
          if (controller.signal.aborted) return;
          setPdfProgress(prev => prev ? { ...prev, step: 'Reconnecting…', status: 'reconnecting' } : prev);
          setTimeout(() => {
            if (pdfSseAbortControllerRef.current) {
              connect();
            }
          }, 1500);
        }
      };

      connect();
    } catch (e) {
      setPdfProgress(prev => prev ? { ...prev, step: 'Error', status: 'error' } : prev);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pdfEventSourceRef.current) {
        try { pdfEventSourceRef.current.close(); } catch (e) {}
        pdfEventSourceRef.current = null;
      }
      if (pdfSseAbortControllerRef.current) {
        try { pdfSseAbortControllerRef.current.abort(); } catch (e) {}
        pdfSseAbortControllerRef.current = null;
      }
    };
  }, []);

  // When processing completes, fetch latest metadata for the processed document
  useEffect(() => {
    const updateMetadataAfterCompletion = async () => {
      if (!pdfProgress || pdfProgress.status !== 'completed' || !pdfProgress.documentId) return;
      try {
        const metadataItem = await documentAPI.getMetadataRecord(pdfProgress.documentId);
        const updatedEntry = {
          documentId: pdfProgress.documentId,
          documentName: pdfProgress.documentName,
          metadata: metadataItem,
          metadataId: metadataItem?.metadataId,
        };
        setDocumentMetadata(prev => {
          const idx = prev.findIndex(m => m.documentId === updatedEntry.documentId);
          if (idx !== -1) {
            const copy = prev.slice();
            copy[idx] = updatedEntry;
            return copy;
          }
          return [updatedEntry, ...prev];
        });
      } catch (err) {
        console.error('Failed to refresh metadata after processing completion:', err);
      }
    };
    updateMetadataAfterCompletion();
  }, [pdfProgress]);

  const dashboardRef = useRef(null);

  // After login, fetch user subscription info
  useEffect(() => {
    if (isAuthenticated && user) {
      api.getUserSubscriptions().then(subRes => {
        if (subRes && subRes.activeSubscription) {
          setUser(prev => ({
            ...prev,
            subscriptionStatus: subRes.activeSubscription.name === 'Trial' ? 'trial' : 'active',
            subscriptionPlan: subRes.activeSubscription.name,
            activeSubscription: subRes.activeSubscription,
          }));
          // If dashboard is open, refresh it
          if (showDashboard && dashboardRef.current && dashboardRef.current.refreshDashboard) {
            dashboardRef.current.refreshDashboard();
          }
        }
      }).catch(err => {
        console.error('Failed to fetch user subscription after login:', err);
      });
    }
  }, [isAuthenticated, user?.id, showDashboard]);

  useEffect(() => {
    function handleExpandSidebar() {
      setSidebarCollapsed(false);
      setShowLeftSidebar(true);
    }
    window.addEventListener('expandSidebar', handleExpandSidebar);
    
    // Handle token expiration events
    function handleTokenExpired() {
      console.log('🔐 Token expired, logging out user');
      logout();
    }
    window.addEventListener('auth:tokenExpired', handleTokenExpired);
    
    return () => {
      window.removeEventListener('expandSidebar', handleExpandSidebar);
      window.removeEventListener('auth:tokenExpired', handleTokenExpired);
    };
  }, [logout]);

  // If user is not authenticated, show the welcome screen or reset password
  if (!isAuthenticated) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="*" element={<WelcomeScreen />} />
        </Routes>
      </BrowserRouter>
    );
  }

  const handleCancelStream = async () => {
    if (currentStreamControl) {
      console.log('Cancelling current stream...');
      
      // Call abort to stop the fetch
      if (currentStreamControl.abort) {
        currentStreamControl.abort();
      }
      
      // Call the backend API to cancel the stream session
      if (currentStreamControl.streamSessionId) {
        try {
          await chatAPI.cancelStream(currentStreamControl.streamSessionId);
          console.log('Stream cancellation sent to backend');
        } catch (error) {
          console.error('Failed to send cancellation to backend:', error);
        }
      }
      
      setCurrentStreamControl(null);
      setIsLoadingRAGResponse(false);
    }
  };

  const handleSend = async (text) => {
    if (!text.trim()) return;

    setIsLoadingRAGResponse(true);

    // Show user message immediately
    const userMsg = {
      sender: "user",
      text,
    };
    
    setMessages((prev) => [...prev, userMsg]);
    
    // Remove thinking placeholder logic

    // Get metadata ID from the first selected document only (refresh from backend first)
    const firstSelectedDoc = selectedDocuments.length > 0 ? selectedDocuments[0] : null;
    let metadataId = null;
    
    if (firstSelectedDoc) {
      // 1) Always try to fetch the latest metadata record by documentId
      try {
        const latestRecord = await documentAPI.getMetadataRecord(firstSelectedDoc.id);
        const latestId = latestRecord?.metadataId || latestRecord?.metadata?.metadataId;
        if (latestId) {
          metadataId = latestId;
          // Update local cache so subsequent sends don't need to refetch
          const updatedEntry = {
            documentId: firstSelectedDoc.id,
            documentName: (availableDocuments.find(d => d.id === firstSelectedDoc.id)?.originalName) || firstSelectedDoc.name || 'Document',
            metadata: latestRecord?.metadata || latestRecord,
            metadataId: latestId,
          };
          setDocumentMetadata(prev => {
            const idx = prev.findIndex(m => m.documentId === updatedEntry.documentId);
            if (idx !== -1) {
              const copy = prev.slice();
              copy[idx] = updatedEntry;
              return copy;
            }
            return [updatedEntry, ...prev];
          });
        }
      } catch (e) {
        // 2) Fallback to any cached metadata if fetch fails
        const cached = documentMetadata.find(meta => meta.documentId === firstSelectedDoc.id);
        metadataId = cached?.metadataId || null;
      }
      // Final fallback to cached if latestRecord returned no id
      if (!metadataId) {
        const cached = documentMetadata.find(meta => meta.documentId === firstSelectedDoc.id);
        metadataId = cached?.metadataId || null;
      }
    }
    
    // If no metadata ID is found, don't use any
    const metadataIds = metadataId ? [metadataId] : [];

    if (metadataIds.length === 0) {
      setIsLoadingRAGResponse(false);
      // Gracefully highlight the document selector when nothing is selected
      if (selectedDocuments.length === 0) {
        try { window.dispatchEvent(new Event('documentSelector:highlight')); } catch (e) {}
      }
      const errorMessage = {
        sender: "ai",
        text: selectedDocuments.length > 0 
          ? "Selected document isn’t ready yet (no metadata). Please wait for processing to complete or choose a different document."
          : "You haven’t selected a document yet. Please pick one from the document dropdown to continue.",
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
      return;
    }

    // Model ID and title for the chat (customize as needed)
    let modelId = "d3be8530-e7e2-4ffd-8ff5-ad9862fcd1ee";
    try {
      const userPreferences = JSON.parse(localStorage.getItem('userPreferences'));
      if (userPreferences && userPreferences.selectedModel) {
        modelId = userPreferences.selectedModel;
      }
    } catch (e) {}
    const title = text.length > 30 ? text.substring(0, 30) + "..." : text;

    // Track the index where we add the streaming message (needs to be in higher scope for error handling)
    let actualStreamingIndex = -1;

    try {
      // Prepare chat API payload
      const chatPayload = {
        question: text,
        metadataIds: metadataIds, // This will contain just the first document's metadataId if available
        title,
        modelId,
      };
      if (currentSessionId) {
        chatPayload.sessionId = currentSessionId;
      }
      
      // Use streaming API for smooth word-by-word responses
      let streamedContent = '';
      
      // Add a placeholder AI message that will be updated with streaming content
      // Use functional update to get the correct current length
      setMessages((prev) => {
        actualStreamingIndex = prev.length; // This will be the index of the new AI message
        return [...prev, {
          sender: "ai",
          text: "",
          isStreaming: true,
          canCancel: true,
        }];
      });

      try {
        const streamControl = await chatAPI.askStream(
          chatPayload,
          // onToken callback - called for each token received
          (token, fullContent) => {
            streamedContent = fullContent;
            setMessages((prev) => {
              const updated = [...prev];
              // Find the last AI message that's streaming (in case of race conditions)
              const streamingIdx = prev.findIndex((m, idx) => idx >= actualStreamingIndex && m.sender === "ai" && m.isStreaming);
              if (streamingIdx !== -1) {
                updated[streamingIdx] = {
                  sender: "ai",
                  text: fullContent,
                  isStreaming: true,
                  canCancel: true,
                };
              }
              return updated;
            });
          },
          // onComplete callback - called when streaming finishes
          (completeData) => {
            // Clear stream control and loading state
            setCurrentStreamControl(null);
            setIsLoadingRAGResponse(false);
            
            const aiMsg = {
              sender: "ai",
              text: completeData.fullContent || streamedContent || "No answer returned.",
              relevantClauses: completeData.relevantClauses || [],
              isStreaming: false,
              canCancel: false,
            };

            // Update the final message
            setMessages((prev) => {
              const updated = [...prev];
              // Find the streaming message and update it
              const streamingIdx = prev.findIndex((m, idx) => idx >= actualStreamingIndex && m.sender === "ai" && m.isStreaming);
              if (streamingIdx !== -1) {
                updated[streamingIdx] = aiMsg;
              }
              return updated;
            });

            // Handle new chat session
            if (!selectedChatId && completeData.sessionId) {
              setSelectedChatId(completeData.sessionId);
              setCurrentSessionId(completeData.sessionId);
              setDocumentRefreshTrigger((prev) => prev + 1);
            }

            // Update chat history
            if (selectedChatId) {
              setChatHistory((prev) =>
                prev.map((chat) =>
                  chat.id === selectedChatId
                    ? {
                        ...chat,
                        messages: [...chat.messages, aiMsg],
                        sessionId: completeData.sessionId || chat.sessionId,
                      }
                    : chat
                )
              );
            }
          },
          // onError callback
          (error) => {
            console.error('Streaming error:', error);
            
            // Clear stream control and loading state
            setCurrentStreamControl(null);
            setIsLoadingRAGResponse(false);
            
            // Extract user-friendly error message
            let errorText = "Sorry, an error occurred while processing your request. Please try again.";
            
            // Check if it's a specific error from the stream
            if (error.isStreamError) {
              errorText = "The AI encountered an issue while generating the response. Please try again or rephrase your question.";
            }
            
            // Check if it was user-cancelled
            if (error.name === 'AbortError') {
              errorText = "Response generation cancelled.";
            }
            
            const errorMessage = {
              sender: "ai",
              text: errorText,
              isError: error.name !== 'AbortError', // Don't show as error if user cancelled
              isStreaming: false,
              canCancel: false,
            };
            setMessages((prev) => {
              const updated = [...prev];
              const streamingIdx = prev.findIndex((m, idx) => idx >= actualStreamingIndex && m.sender === "ai" && m.isStreaming);
              if (streamingIdx !== -1) {
                updated[streamingIdx] = errorMessage;
              }
              return updated;
            });
          },
          // onSession callback - called when session info is received
          (sessionInfo) => {
            console.log('Stream session started:', sessionInfo);
          }
        );
        
        // Store the stream control for cancellation
        setCurrentStreamControl(streamControl);
      } catch (error) {
        // Fallback to original non-streaming API if stream fails
        console.warn('Streaming failed, falling back to regular API:', error);
        
        try {
          const response = await chatAPI.ask(chatPayload);

          // Prepare real AI message
          const aiMsg = {
            sender: "ai",
            text: response.answerMessage?.content || response.answer || "No answer returned.",
            relevantClauses: response.relevantClauses || [],
          };

          // Update the streaming message with the full response
          setMessages((prev) => {
            const updated = [...prev];
            const streamingIdx = prev.findIndex((m, idx) => idx >= actualStreamingIndex && m.sender === "ai");
            if (streamingIdx !== -1) {
              updated[streamingIdx] = aiMsg;
            }
            return updated;
          });

          if (!selectedChatId) {
            setSelectedChatId(response.sessionId);
            setCurrentSessionId(response.sessionId);
            setDocumentRefreshTrigger((prev) => prev + 1);
            // Immediately load and display the messages for the new session using unified approach
            const session = await chatSessionsAPI.getSessionById(response.sessionId);
            if (session && session.messages) {
              // Process each message to include relevant clauses
              const processedMessages = [];
              
              for (const msg of session.messages) {
                // Add user message
                if (msg.question?.content) {
                  processedMessages.push({
                    sender: "user",
                    text: msg.question.content,
                  });
                }
                
                // Add AI message with relevant clauses
                if (msg.answer?.content) {
                  // Check if relevantClauses already exist in the stored message
                  let relevantClauses = msg.answer.metadata?.relevantClauses || msg.answer.relevantClauses || msg.relevantClauses || [];
                  
                  // If no clauses exist, fetch them
                  if (relevantClauses.length === 0 && msg.question?.content) {
                    relevantClauses = await fetchRelevantClauses(msg.question.content, msg.answer.content);
                  }
                  
                  processedMessages.push({
                    sender: "ai",
                    text: msg.answer.content,
                    relevantClauses: relevantClauses,
                  });
                }
              }
              
              setMessages(processedMessages);
            } else {
              setMessages([]);
            }
          } else {
            setChatHistory((prev) =>
              prev.map((chat, idx) =>
                chat.id === selectedChatId
                  ? {
                      ...chat,
                      messages: [...chat.messages, aiMsg],
                      sessionId: response.sessionId || chat.sessionId,
                    }
                  : chat
              )
            );
          }
        } catch (fallbackError) {
          if (fallbackError?.data?.error === "Chat limit reached") {
            setShowChatLimitModal(true);
            setIsChatLimitAdmin(user?.role === 'admin' || user?.role === 'super_admin');
            setIsLoadingRAGResponse(false);
            return;
          }
          // Replace streaming message with error
          const errorMessage = {
            sender: "ai",
            text:
              fallbackError.message ||
              "Sorry, I encountered an error while processing your question. Please try again.",
            isError: true,
          };
          setMessages((prev) => {
            const updated = [...prev];
            const streamingIdx = prev.findIndex((m, idx) => idx >= actualStreamingIndex && m.sender === "ai");
            if (streamingIdx !== -1) {
              updated[streamingIdx] = errorMessage;
            }
            return updated;
          });
        }
      }
    } catch (error) {
      if (error?.data?.error === "Chat limit reached") {
        setShowChatLimitModal(true);
        setIsChatLimitAdmin(user?.role === 'admin' || user?.role === 'super_admin');
        setIsLoadingRAGResponse(false);
        return;
      }
      // Replace streaming message with error
      const errorMessage = {
        sender: "ai",
        text:
          error.message ||
          "Sorry, I encountered an error while processing your question. Please try again.",
        isError: true,
      };
      setMessages((prev) => {
        const updated = [...prev];
        const streamingIdx = prev.findIndex((m, idx) => idx >= actualStreamingIndex && m.sender === "ai");
        if (streamingIdx !== -1) {
          updated[streamingIdx] = errorMessage;
          return updated;
        }
        // If no streaming message found, add error message
        return [...prev, errorMessage];
      });
      if (selectedChatId) {
        setChatHistory((prev) =>
          prev.map((chat) =>
            chat.id === selectedChatId
              ? { ...chat, messages: [...chat.messages, errorMessage] }
              : chat
          )
        );
      }
    } finally {
      setIsLoadingRAGResponse(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100 dark:bg-gray-900 transition-colors duration-300">
      <TopBar
        onLeftToggle={() => {
          if (!showLeftSidebar) setShowLeftSidebar(true);
          else setSidebarCollapsed((prev) => !prev);
        }}
        onOpenSettings={() => setShowSettings(true)}
        onOpenFiles={() => setShowFilesModal(true)}
        onOpenLogin={() => {
          setShowLogin(true);
        }}
        onOpenRegister={() => {
          setShowRegister(true);
        }}
        onOpenPayment={() => {
          setShowPayment(true);
        }}
        onShowLogoutConfirm={() => setShowLogoutConfirm(true)}
        pdfProgress={pdfProgress}
      />
      <div className="flex flex-1 overflow-hidden transition-all duration-500 ease-in-out">
        {/* Left Sidebar */}
        <div
          className={`h-full overflow-x-hidden ${showLeftSidebar ? '' : 'opacity-0 pointer-events-none'}`}
          style={{
            width: showLeftSidebar ? (sidebarCollapsed ? 64 : 256) : 64,
            transition: 'width 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease',
          }}
        >
          <LeftSidebar
            chatHistory={chatHistory}
            selectedChatId={selectedChatId}
            onSelectChat={handleChatSelect}
            refreshTrigger={documentRefreshTrigger}
            onOpenDocumentManager={() => setShowFilesModal(true)}
            onCreateNewChat={handleCreateNewChat}
            onOpenPayment={() => setShowPayment(true)}
            collapsed={sidebarCollapsed}
            onDashboardClick={() => setShowDashboard(true)}
          />
        </div>
        {/* Main Content */}
        <div className="flex flex-col flex-1 overflow-hidden">
          {showDashboard ? (
            <div className="relative h-full w-full overflow-hidden">
              <Dashboard ref={dashboardRef} />
            </div>
          ) : (
            <>
              <ChatWindow 
                messages={messages} 
                isLoadingResponse={isLoadingRAGResponse} 
                selectedChatId={selectedChatId}
                availableDocuments={availableDocuments}
                documentMetadata={documentMetadata}
                selectedCodeBook={selectedCodeBook}
                setSelectedCodeBook={setSelectedCodeBook}
                selectedDocuments={selectedDocuments}
                onDocumentSelectionChange={setSelectedDocuments}
                onCancelStream={handleCancelStream}
              />
              <MessageInput 
                onSend={handleSend} 
                isStreaming={messages.some(msg => msg.isStreaming)}
                onCancelStream={handleCancelStream}
              />
            </>
          )}
        </div>


      </div>
       {/* ✅ Settings Modal at the top level */}
    <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />
    <FilesModal 
      isOpen={showFilesModal} 
      onClose={() => setShowFilesModal(false)} 
      onDocumentChange={refreshDocuments}
      onStartPdfProgress={startPdfProcessingProgress}
      pdfProgress={pdfProgress}
    />
    <LoginScreen 
      isOpen={showLogin}
      onClose={() => setShowLogin(false)} 
      onSwitchToRegister={() => {
        setShowLogin(false);
        setShowRegister(true);
      }}
    />
    <RegisterScreen 
      isOpen={showRegister}
      onClose={() => setShowRegister(false)} 
      onSwitchToLogin={() => {
        setShowRegister(false);
        setShowLogin(true);
      }}
    />
    <PaymentScreen 
      isOpen={showPayment} 
      onClose={() => setShowPayment(false)} 
      onPaymentSuccess={() => {
        if (showDashboard && dashboardRef.current && dashboardRef.current.refreshDashboard) {
          dashboardRef.current.refreshDashboard();
        }
        setShowPayment(false);
        setShowThankYou(true);
      }}
    />
    <ChatLimitModal
      isOpen={showChatLimitModal}
      isAdmin={isChatLimitAdmin}
      onClose={() => setShowChatLimitModal(false)}
      onUpgrade={() => {
        setShowChatLimitModal(false);
        setShowPayment(true);
      }}
    />
    <ThankYouModal isOpen={showThankYou} onClose={() => setShowThankYou(false)} />
    <EmployeesModal isOpen={showEmployeesModal} onClose={() => setShowEmployeesModal(false)} />
    {/* Place the ConfirmationModal at the root so it is not unmounted on logout */}
    <ConfirmationModal
      isOpen={showLogoutConfirm}
      onClose={() => setShowLogoutConfirm(false)}
      onConfirm={handleLogoutConfirm}
      title="Confirm Logout"
      message="Are you sure you want to logout? Your session will end and you'll need to login again."
      confirmText="Yes, Logout"
      cancelText="Cancel"
    />


    </div>
  );
}
